/**
 * Output. CONCEPT.md section 7.
 *
 * The volume panels behind the quote, shown in Expert mode: how the peak month
 * was reached, where it comes from and how it moves month by month. Still
 * absent: prices, utilisation, overage warnings. A billing system handles
 * withdrawal, and this tool cannot get a bill wrong if it never computes one.
 */

import {
  DEFAULT_RETENTION_DAYS,
  type Finding,
  type MonthResult,
  type PeriodResult,
  type Scenario,
  type ScenarioResult,
} from '../../lib/engine/index.js';
import { compact, gib, gibMonths, gibRange, monthYear, n, nf1, pct, rate, signed } from './format.js';
import { Rich, useT } from './i18n.js';

export function Results({ scenario, result }: { scenario: Scenario; result: ScenarioResult }) {
  const t = useT();
  const peak = result.peakMonth;
  const steady = result.months.filter((m) => m.onboardingCreates === 0);
  const steadyPeak = steady.reduce<MonthResult | undefined>(
    (best, m) => (!best || m.total > best.total ? m : best),
    undefined,
  );
  const steadyLean = steady.reduce<MonthResult | undefined>(
    (best, m) => (!best || m.total < best.total ? m : best),
    undefined,
  );

  return (
    <>
      <div class="grid four" style="margin-bottom:18px">
        <div class="stat">
          <span>{t('results.stat.peakMonth')}</span>
          <b>{compact(peak.total)}</b>
          <small>
            {t('results.stat.peakMonth.sub', {
              month: monthYear(peak.year, peak.month),
              days: peak.days,
            })}
            {peak.onboardingCreates > 0 &&
              t('results.stat.peakMonth.registrations', { count: n(peak.onboardingCreates) })}
          </small>
        </div>
        <div class="stat">
          <span>{t('results.stat.range')}</span>
          <b>
            {steadyLean && steadyPeak
              ? `${compact(steadyLean.total)} – ${compact(steadyPeak.total)}`
              : '—'}
          </b>
          <small>
            {steadyLean && steadyPeak && steadyLean.total > 0
              ? t('results.stat.range.swing', {
                  pct: nf1.format((steadyPeak.total / steadyLean.total - 1) * 100),
                })
              : t('results.stat.range.same')}
          </small>
        </div>
        <div class="stat">
          <span>{t('results.stat.perMachine')}</span>
          <b>{n(peak.perMachinePerMonth)}</b>
          <small>{t('results.stat.perMachine.sub')}</small>
        </div>
        <div class="stat">
          <span>{t('results.stat.perSecond')}</span>
          <b>{rate(peak.avgMessagesPerSec)}</b>
          <small>{t('results.stat.perSecond.sub')}</small>
        </div>
      </div>

      <div class="grid two">
        <ByMachineType peak={peak} />
        <Ramp periods={result.periods} />
      </div>

      <Findings findings={result.findings} />

      <p class="disclaimer">
        <Rich k="results.disclaimer" />
      </p>
    </>
  );
}

function ByMachineType({ peak }: { peak: MonthResult }) {
  const t = useT();
  const rows = [...peak.byMachineType].sort((a, b) => b.total - a.total);
  const max = Math.max(1, ...rows.map((r) => r.total));

  return (
    <section class="panel">
      <header>
        <h2>{t('byType.heading')}</h2>
      </header>
      <div class="body tight">
        {rows.length === 0 ? (
          <div class="empty">{t('byType.empty')}</div>
        ) : (
          <table>
            <tbody>
              {rows.map((row) => (
                <tr key={row.machineTypeId}>
                  <td>
                    {row.name}
                    <div class="bar">
                      <i style={`width:${(row.total / max) * 100}%`} />
                    </div>
                  </td>
                  <td class="num" style="width:170px">
                    {compact(row.total)}
                    <div style="font-size:11px;color:var(--ink-faint)">
                      {peak.total > 0 ? n((row.total / peak.total) * 100) : 0} % ·{' '}
                      {t('byType.perSecond', { rate: rate(row.avgMessagesPerSec) })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function Ramp({ periods }: { periods: PeriodResult[] }) {
  const t = useT();
  const months = periods.flatMap((p) => p.months);
  const max = Math.max(1, ...months.map((m) => m.total));

  return (
    <section class="panel">
      <header>
        <h2>{t('ramp.heading')}</h2>
        <span class="sub">{t.plural('ramp.months', months.length)}</span>
      </header>
      <div class="body">
        <div class="ramp">
          {months.map((m) => (
            <i
              key={`${m.year}-${m.month}`}
              class={m.total === max ? 'peak' : ''}
              style={`height:${(m.total / max) * 100}%`}
              title={t('ramp.tooltip', {
                month: monthYear(m.year, m.month),
                count: n(m.total),
                days: m.days,
              })}
            />
          ))}
        </div>
        <table style="margin-top:12px">
          <tbody>
            {periods.map((p) => (
              <tr key={p.index}>
                <td>
                  {t('contract.periodN', { index: p.index })}
                  <div style="font-size:11.5px;color:var(--ink-faint)">
                    {t.plural('ramp.monthsFrom', p.months.length, {
                      month: p.months[0] ? monthYear(p.months[0].year, p.months[0].month) : '—',
                    })}
                  </div>
                </td>
                <td class="num">
                  {compact(p.lean.total)} – {compact(p.peak.total)}
                  <div style="font-size:11.5px;color:var(--ink-faint)">
                    {t('ramp.peak', { month: monthYear(p.peak.year, p.peak.month) })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function Findings({ findings }: { findings: Finding[] }) {
  const t = useT();
  const errors = findings.filter((f) => f.severity === 'error').length;
  const warnings = findings.filter((f) => f.severity === 'warning').length;

  return (
    <section class="panel">
      <header>
        <h2>{t('findings.heading')}</h2>
        <span class="sub">
          {findings.length === 0
            ? t('findings.nothing')
            : [
                t.plural('findings.errors', errors),
                t.plural('findings.warnings', warnings),
                t.plural('findings.suggestions', findings.length - errors - warnings),
              ].join(', ')}
        </span>
      </header>
      <div class="body tight">
        {findings.length === 0 ? (
          <div class="clean">{t('findings.clean')}</div>
        ) : (
          findings.map((f, i) => (
            <div class={`finding ${f.severity}`} key={`${f.rule}-${i}`}>
              <span class="rule">{f.rule}</span>
              <div class="txt">
                <b>{t(f.titleKey, f.titleParams)}</b>
                <p>{t(f.detailKey, f.detailParams)}</p>
              </div>
              {f.messageDelta !== undefined && Math.abs(f.messageDelta) >= 1 && (
                <span class={`delta ${f.messageDelta < 0 ? 'saves' : 'costs'}`}>
                  {signed(f.messageDelta)}
                  <div style="font-size:10px;font-weight:400;color:var(--ink-faint);text-align:right">
                    {t('findings.perMonth')}
                  </div>
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
}

/**
 * Operational storage: a range, and why it is a range.
 *
 * The two figures behind it are rules of thumb marked "to be verified" at
 * source, and one of them spans 4x on its own. Reporting a midpoint would make
 * that look like a measurement, so both ends are shown, the retention that
 * scales them is stated, and the ODS line stays somebody's decision.
 *
 * The headline figure is a period's fullest month-end in whole GiB, because
 * that is what the ODS line holds and the Configurator multiplies by the
 * months. The fullest month keeps a stat of its own: it says when the fleet
 * stopped filling up, and a period whose peak lands in its last month is a
 * period that will be quoted higher next time.
 */
export function Storage({ result }: { result: ScenarioResult }) {
  const t = useT();
  const peak = result.peakStorage;
  // The quote is anchored on the first period, which is what the Configurator's
  // own note says; the others are listed under the grid.
  const first = result.storageByPeriod[0];
  if (!peak || !first || !first.peak || peak.retained <= 0) return null;
  const firstPeak = first.peak;
  const partial = peak.daysCovered < peak.retentionDays;
  const mixed = peak.retentionDaysShortest !== peak.retentionDays;
  // What share of the retained volume is not measurement values: event, alarm
  // and operation documents plus every managed object registered so far.
  const otherShare = peak.retained > 0 ? peak.retainedOther / peak.retained : 0;
  const keptFor = mixed
    ? t('storage.kept.mixed', {
        from: n(peak.retentionDaysShortest),
        to: n(peak.retentionDays),
      })
    : t('storage.kept.uniform', { days: n(peak.retentionDays) });

  return (
    <section class="panel">
      <header>
        <h2>{t('storage.heading')}</h2>
        <span class="sub">{t('storage.sub')}</span>
      </header>
      <div class="body">
        <div class="grid four" style="margin-bottom:18px">
          <div class="stat">
            {/* The unit rides in the label rather than the figure: "778
                GiB-months" breaks across its own hyphen at this size, and a
                unit in small caps above the number is where a reader looks for
                one anyway. */}
            <span>{t('storage.stat.ods', { index: n(first.periodIndex) })}</span>
            <b>{n(first.unitsPerMonth)}</b>
            <small>
              {t('storage.stat.ods.sub', {
                range: gibRange(firstPeak.lowGiB, firstPeak.highGiB),
              })}
            </small>
          </div>
          <div class="stat">
            <span>{t('storage.stat.fullest')}</span>
            <b>{monthYear(peak.year, peak.month)}</b>
            <small>
              {t('storage.stat.fullest.sub', { kept: keptFor })}
              {partial &&
                t('storage.stat.fullest.partial', { days: n(peak.daysCovered) })}
            </small>
          </div>
          <div class="stat">
            <span>{t('storage.stat.onDisk')}</span>
            <b>{compact(peak.retained)}</b>
            <small>{t('storage.stat.onDisk.sub', { count: compact(peak.written) })}</small>
          </div>
          <div class="stat">
            <span>{t('storage.stat.perMeasurement')}</span>
            <b>{nf1.format(peak.valuesPerMeasurement)}</b>
            <small>
              {peak.valuesPerMeasurement > 1.5
                ? t('storage.stat.perMeasurement.shared', {
                    count: nf1.format(peak.valuesPerMeasurement),
                  })
                : t('storage.stat.perMeasurement.alone')}
            </small>
          </div>
          <div class="stat">
            {/* The same month as the ODS stat. Two figures on two different
                bases in one grid read as a contradiction rather than as two
                facts. */}
            <span>{t('storage.stat.dataHub', { index: n(first.periodIndex) })}</span>
            <b>{gibRange(firstPeak.dataHubLowGiB, firstPeak.dataHubHighGiB)}</b>
            <small>{t('storage.stat.dataHub.sub')}</small>
          </div>
        </div>

        {result.storageByPeriod.length > 1 && (
          <p class="hint" style="margin:-8px 0 16px">
            {t('storage.perPeriod')}{' '}
            {result.storageByPeriod
              .map((p) => `P${p.periodIndex} ${gib(p.unitsPerMonth)}`)
              .join(' · ')}
          </p>
        )}

        <div class="grid two">
          <div>
            <p class="note" style="margin:0">
              <Rich
                k="storage.odsCell"
                p={{
                  amount: gib(first.unitsPerMonth),
                  index: n(first.periodIndex),
                  months: n(first.monthsCounted),
                  average: gib(first.averageGiB),
                  note: gibMonths(first.giBMonths) + '. ' + t('engine.storageSourceNote'),
                }}
              />
            </p>
            <p style="font-size:13px;color:var(--ink-mute);margin-top:10px">
              {t('storage.bundlingHelps', { count: nf1.format(peak.valuesPerMeasurement) })}
            </p>
          </div>
          <div>
            <p style="font-size:13px;color:var(--ink-mute);margin-top:0">
              <Rich
                k="storage.retentionDecides"
                p={{
                  kept: keptFor,
                  note:
                    !mixed && peak.retentionDays === DEFAULT_RETENTION_DAYS
                      ? t('storage.retentionDefault')
                      : '',
                }}
              />
            </p>
            {/* The byte figure was measured on datapoints, so the part of the
                estimate that is documents rests on the weaker assumption. It
                is counted anyway -- leaving it out understates the bill, and on
                a commit-to-consume contract that is the expensive direction --
                so its share is stated instead of being quietly carried. */}
            <p style="font-size:13px;color:var(--ink-mute)">
              <Rich
                k="storage.documentsToo"
                p={{
                  share:
                    otherShare < 0.01 ? t('storage.under1pct') : pct(otherShare),
                }}
              />
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
